// Shopify theme sync marker: listing-rewrite-2026-06-21.
import { Component } from '@theme/component';
import { createSoAtlantaClient, normalizeRailItem, readJsonScript } from '@theme/so-atlanta-api';
import {
  getFreshLocationIfAlreadyGranted,
  getGeolocationPermissionState,
  isGeolocationAvailable,
  requestFreshLocationFromUser,
} from '@theme/so-atlanta-location';
import { renderDisclosure, renderEmpty, renderError, renderLocationCard, setHtml } from '@theme/so-atlanta-renderers';

const INITIAL_VISIBLE_VIDEO_LOADS = 6;
const VIDEO_PREFETCH_AHEAD = 3;
const PRIORITY_VISIBLE_VIDEO_LOADS = 2;
const HOMEPAGE_RAIL_PAGE_LIMIT = 9;
const HOMEPAGE_RAIL_MAX_ITEMS = 50;
const RAIL_MARQUEE_MIN_DURATION_SECONDS = 84;
const RAIL_MARQUEE_TARGET_PIXELS_PER_SECOND = 40;
const CATALOG_HEADING_TEXT = 'TAKE HOME A PIECE OF THE CITY';

class SoAtlantaHomepageRails extends Component {
  settings = {};
  client = null;
  connected = false;
  userLocation = null;
  lastRails = null;
  autoScrollers = [];
  activeVideoLoads = 0;
  maxActiveVideoLoads = INITIAL_VISIBLE_VIDEO_LOADS;
  videoLoadTimers = new Map();
  videoFrames = new Map();
  videoLoading = new WeakSet();
  videoObserver = null;
  videoQueue = [];
  loadingMoreRails = false;
  personalizationParams = {};

  #handleTikTokMessage = (event) => {
    if (event.origin !== 'https://www.tiktok.com') return;
    const data = event.data;
    if (!data || data['x-tiktok-player'] !== true) return;

    const tracked = this.#findTrackedVideo(event.source);
    if (!tracked) return;

    if (data.type === 'onPlayerError') {
      this.#resetVideoFallback(tracked.video, 'player-error');
      return;
    }

    if (isPlayerReadyMessage(data.type)) {
      this.#markVideoPlaying(tracked.video, tracked.iframe);
    }
  };

  connectedCallback() {
    super.connectedCallback();
    this.connected = true;
    this.settings = readJsonScript(this, '[data-sa-homepage-settings]');
    this.client = createSoAtlantaClient(this.settings);
    window.addEventListener('message', this.#handleTikTokMessage);
    linkHomepageCatalogHeading();
    void this.#initializeLocation();
  }

  disconnectedCallback() {
    this.connected = false;
    window.removeEventListener('message', this.#handleTikTokMessage);
    this.#teardownVideos();
    this.#teardownAutoScroll();
    super.disconnectedCallback();
  }

  #notifyRailsLoaded(status) {
    window.requestAnimationFrame(() => {
      window.dispatchEvent(
        new CustomEvent('so-atlanta:homepage-rails-loaded', {
          detail: { status },
        })
      );
    });
  }

  async #initializeLocation() {
    if (this.settings.enabled === false) {
      await this.#load(null);
      return;
    }
    if (!isGeolocationAvailable()) {
      await this.#load(null);
      return;
    }

    const permissionState = await getGeolocationPermissionState();
    if (!this.connected) return;
    if (permissionState === 'denied') {
      await this.#load(null);
      return;
    }

    const location = permissionState === 'granted'
      ? await getFreshLocationIfAlreadyGranted({ timeout: 6000 })
      : await requestFreshLocationFromUser({ timeout: 10000 }).catch(() => null);
    if (!this.connected) return;
    await this.#load(location);
  }

  /**
   * @param {{ latitude: number, longitude: number } | null} location
   */
  async #load(location) {
    const content = this.querySelector('[data-rails-content]');
    if (!(content instanceof HTMLElement)) {
      this.#notifyRailsLoaded('missing-content');
      return;
    }

    if (this.settings.enabled === false) {
      content.removeAttribute('aria-busy');
      setHtml(content, renderEmpty(this.settings.emptyText));
      this.#notifyRailsLoaded('disabled');
      return;
    }

    this.userLocation = location;
    this.personalizationParams = location ? roundedLocationParams(location) : {};
    content.setAttribute('aria-busy', 'true');

    try {
      const data = await this.client.homepage(this.personalizationParams);
      if (!this.connected) return;
      const rails = Array.isArray(data?.rails) ? data.rails : [];
      const homepageRails = this.#mergeFeaturedRail(rails);
      if (!homepageRails.length) {
        content.removeAttribute('aria-busy');
        setHtml(content, renderEmpty(this.settings.emptyText));
        this.#notifyRailsLoaded('empty');
        return;
      }
      this.lastRails = homepageRails;
      content.removeAttribute('aria-busy');
      this.#teardownVideos();
      setHtml(content, homepageRails.map((rail) => this.#renderRail(rail)).filter(Boolean).join(''));
      this.#setupAutoScroll();
      this.#observeAutoVideos();
      this.#notifyRailsLoaded('loaded');
      void this.#loadMoreRailPages();
    } catch (error) {
      console.error('So Atlanta homepage rails failed', error);
      if (!this.connected) return;
      content.removeAttribute('aria-busy');
      setHtml(content, renderError(this.settings.errorText));
      this.#notifyRailsLoaded('error');
    }
  }

  /**
   * @param {any[]} rails
   * @returns {any[]}
   */
  #mergeFeaturedRail(rails) {
    const personalizedRail = rails.find((rail) => rail?.id === 'trending' && rail?.personalized === true);
    if (personalizedRail) return rails.filter((rail) => rail?.id !== 'featured');

    const featuredRail = rails.find((rail) => rail?.id === 'featured');
    const featuredItems = Array.isArray(featuredRail?.items)
      ? featuredRail.items.map(markFeaturedItem)
      : [];
    if (!featuredItems.length) return rails;

    const featuredSlugs = new Set(featuredItems.map(itemSlug).filter(Boolean));
    const targetRail = rails.find((rail) => rail?.id === 'trending');
    if (!targetRail) {
      return rails.map((rail) =>
        rail?.id === 'featured'
          ? { ...rail, title: 'Featured & Trending Around Atlanta', items: featuredItems }
          : rail
      );
    }

    const trendingRanks = new Map(
      (Array.isArray(targetRail.items) ? targetRail.items : [])
        .map((item) => [itemSlug(item), Number(item?.rank)])
        .filter(([slug, rank]) => slug && Number.isFinite(rank))
    );
    const featuredWithTrendingRanks = featuredItems.map((item) => {
      const rank = trendingRanks.get(itemSlug(item));
      return Number.isFinite(rank) ? { ...item, trendingRank: rank } : item;
    });

    const mergedItems = dedupeRailItems([
      ...featuredWithTrendingRanks,
      ...(Array.isArray(targetRail.items) ? targetRail.items.map((item) => (
        featuredSlugs.has(itemSlug(item)) ? markFeaturedItem(item) : item
      )) : []),
    ]);

    return rails
      .filter((rail) => rail?.id !== 'featured')
      .map((rail) =>
        rail?.id === targetRail.id
          ? {
              ...rail,
              title: 'Featured & Trending Around Atlanta',
              items: mergedItems,
            }
          : rail
      );
  }

  /** @param {any} rail */
  #renderRail(rail) {
    let items = Array.isArray(rail?.items) ? rail.items.map(normalizeRailItem) : [];
    if (rail?.id === 'trending') {
      items = items.filter((item) => item.featuredVideo);
    }
    if (!items.length) return '';

    const railDisclosureTip = rail?.personalized === true
      ? 'Recommendations are ordered in regional distance bands using trend, featured, and editorial signals. Atlanta’s citywide #1 keeps its Trending badge wherever it appears. TikTok media is third-party.'
      : 'Trending ranks use 24-hour TikTok momentum first, with featured, location, and editorial signals as context. TikTok media is third-party.';

    return `
      <section class="sa-home-rail" aria-labelledby="sa-rail-${escapeId(rail.id)}" data-sa-rail-id="${escapeHtml(rail.id || 'rail')}">
        <div class="sa-home-rail__header">
          <h3 class="sa-home-rail__title" id="sa-rail-${escapeId(rail.id)}">
            ${escapeHtml(rail.title || 'So Atlanta')}
            ${renderDisclosure('Recommendation', railDisclosureTip, { className: 'sa-home-rail__disclosure sa-disclosure--icon-only sa-disclosure--above' })}
          </h3>
        </div>
        <div class="sa-home-rail__track" data-layout="${escapeHtml(rail.layout || 'horizontal_scroll')}" data-sa-auto-track>
          <div class="sa-home-rail__strip">
            <div class="sa-home-rail__set" data-sa-loop-set>
              ${this.#renderCards(items, false, rail?.personalized === true)}
            </div>
            ${this.#renderCloneSets(items, rail?.personalized === true)}
          </div>
        </div>
      </section>
    `;
  }

  /**
   * @param {ReturnType<typeof normalizeRailItem>[]} items
   * @param {boolean} [clone]
   * @param {boolean} [personalized]
   * @returns {string}
   */
  #renderCards(items, clone = false, personalized = false) {
    return items
      .map((item) =>
        renderLocationCard(item.business, {
          featuredVideo: item.featuredVideo,
          rank: personalized
            ? (item.trendingRank === 1 ? 1 : null)
            : item.trendingRank ?? (item.rankReason === 'editorial_featured' ? null : item.rank),
          variant: 'rail',
          userLocation: this.userLocation,
          clone,
          lazyVideo: true,
        })
      )
      .join('');
  }

  async #loadMoreRailPages() {
    if (this.loadingMoreRails || !this.client || !Array.isArray(this.lastRails)) return;
    this.loadingMoreRails = true;

    try {
      let rail = this.lastRails.find((item) => item?.id === 'trending');
      while (this.connected && rail?.pagination?.hasMore) {
        const pagination = rail.pagination || {};
        const nextOffset = Number(
          pagination.nextOffset ?? Number(pagination.offset || 0) + Number(pagination.limit || HOMEPAGE_RAIL_PAGE_LIMIT)
        );
        if (!Number.isFinite(nextOffset) || nextOffset >= HOMEPAGE_RAIL_MAX_ITEMS) return;

        await waitForRailPageQueue();
        if (!this.connected) return;

        let page = null;
        try {
          page = await this.client.homepageRail('trending', {
            limit: HOMEPAGE_RAIL_PAGE_LIMIT,
            offset: nextOffset,
            ...this.personalizationParams,
          });
        } catch (error) {
          console.warn('So Atlanta homepage rail page failed', error);
          return;
        }

        this.#appendRailPage(page);
        rail = this.lastRails.find((item) => item?.id === 'trending');
      }
    } finally {
      this.loadingMoreRails = false;
    }
  }

  /** @param {any} page */
  #appendRailPage(page) {
    const railId = String(page?.id || '');
    const pageItems = Array.isArray(page?.items) ? page.items : [];
    if (!railId || !Array.isArray(this.lastRails)) return;

    const railIndex = this.lastRails.findIndex((rail) => rail?.id === railId);
    if (railIndex < 0) return;

    const rail = this.lastRails[railIndex];
    const existingSlugs = new Set((Array.isArray(rail.items) ? rail.items : []).map(itemSlug).filter(Boolean));
    const nextItems = pageItems.filter((item) => {
      const slug = itemSlug(item);
      if (!slug || existingSlugs.has(slug)) return false;
      existingSlugs.add(slug);
      return true;
    });

    this.lastRails[railIndex] = {
      ...rail,
      items: [...(Array.isArray(rail.items) ? rail.items : []), ...nextItems],
      pagination: page?.pagination || rail.pagination,
    };

    const renderableItems = nextItems.map(normalizeRailItem).filter((item) => item.featuredVideo);
    if (!renderableItems.length) return;

    const railElement = this.#railElement(railId);
    const originalSet = railElement?.querySelector('[data-sa-loop-set]');
    if (!(originalSet instanceof HTMLElement)) return;

    originalSet.insertAdjacentHTML('beforeend', this.#renderCards(renderableItems, false, rail?.personalized === true));
    railElement.querySelectorAll('[data-sa-clone-set]').forEach((cloneSet) => {
      if (cloneSet instanceof HTMLElement) {
        cloneSet.insertAdjacentHTML('beforeend', this.#renderCards(renderableItems, true, rail?.personalized === true));
      }
    });

    this.#setupAutoScroll();
    this.#observeAutoVideos();
  }

  /** @param {string} railId */
  #railElement(railId) {
    return [...this.querySelectorAll('[data-sa-rail-id]')].find(
      (element) => element instanceof HTMLElement && element.dataset.saRailId === railId
    );
  }

  #observeAutoVideos() {
    this.#disconnectVideoObserver();
    const trackVideoSets = [...this.querySelectorAll('[data-sa-auto-track]')]
      .filter((track) => track instanceof HTMLElement)
      .map((track) => ({
        videos: [...track.querySelectorAll('[data-sa-video-auto]')].filter(
          (video) => video instanceof HTMLElement && !video.closest('.sa-card--clone')
        ),
      }))
      .filter(({ videos }) => videos.length);
    if (!trackVideoSets.length) return;

    if (!('IntersectionObserver' in window)) {
      trackVideoSets.forEach(({ videos }) => {
        videos.slice(0, INITIAL_VISIBLE_VIDEO_LOADS).forEach((video, index) => {
          if (index < PRIORITY_VISIBLE_VIDEO_LOADS) video.dataset.loadPriority = 'true';
          this.#queueVideo(video);
        });
      });
      return;
    }

    const stateByVideo = new WeakMap();
    const observeAhead = (state) => {
      while (state.pendingAhead < VIDEO_PREFETCH_AHEAD && state.nextIndex < state.videos.length) {
        const video = state.videos[state.nextIndex];
        state.nextIndex += 1;
        state.pendingAhead += 1;
        stateByVideo.set(video, state);
        this.videoObserver?.observe(video);
      }
    };

    this.videoObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.target instanceof HTMLElement) {
            const state = stateByVideo.get(entry.target);
            this.videoObserver?.unobserve(entry.target);
            stateByVideo.delete(entry.target);
            if (state) state.pendingAhead = Math.max(0, state.pendingAhead - 1);
            this.#queueVideo(entry.target);
            if (state) observeAhead(state);
          }
        });
      },
      { root: null, rootMargin: '240px 20%', threshold: 0.1 }
    );

    trackVideoSets.forEach(({ videos }) => {
      videos.slice(0, INITIAL_VISIBLE_VIDEO_LOADS).forEach((video, index) => {
        if (index < PRIORITY_VISIBLE_VIDEO_LOADS) video.dataset.loadPriority = 'true';
        this.#queueVideo(video);
      });
      observeAhead({
        videos,
        nextIndex: INITIAL_VISIBLE_VIDEO_LOADS,
        pendingAhead: 0,
      });
    });
  }

  /** @param {HTMLElement} video */
  #queueVideo(video) {
    if (video.dataset.loaded === 'true' || video.dataset.fallback === 'true' || video.dataset.queued === 'true') return;
    video.dataset.queued = 'true';
    this.videoQueue.push(video);
    this.#pumpVideoQueue();
  }

  #pumpVideoQueue() {
    while (this.activeVideoLoads < this.maxActiveVideoLoads && this.videoQueue.length) {
      const video = this.videoQueue.shift();
      if (!(video instanceof HTMLElement)) continue;
      video.dataset.queued = 'false';
      if (!this.connected || video.dataset.loaded === 'true' || video.dataset.fallback === 'true') continue;
      this.#loadVideo(video);
    }
  }

  /** @param {HTMLElement} video */
  #loadVideo(video) {
    if (video.dataset.loaded === 'true' || video.dataset.fallback === 'true') return;

    const src = video.dataset.tiktokEmbedUrl || '';
    if (!src) return;

    const iframe = document.createElement('iframe');
    const isPriorityLoad = video.dataset.loadPriority === 'true';
    iframe.className = 'sa-video__iframe';
    iframe.src = src;
    iframe.title = video.dataset.tiktokTitle || 'TikTok video';
    iframe.loading = isPriorityLoad ? 'eager' : 'lazy';
    if (isPriorityLoad) iframe.setAttribute('fetchpriority', 'high');
    iframe.allow = 'autoplay; encrypted-media';
    iframe.setAttribute('allowfullscreen', '');
    iframe.setAttribute('frameborder', '0');

    video.dataset.loaded = 'true';
    video.dataset.playerReady = 'false';
    this.activeVideoLoads += 1;
    this.videoLoading.add(video);
    video.classList.add('is-loading');
    video.append(iframe);
    this.videoFrames.set(video, iframe);

    const fallbackTimer = window.setTimeout(() => {
      if (iframe.dataset.ready !== 'true') this.#resetVideoFallback(video, 'no-player-message');
    }, 8_000);
    this.videoLoadTimers.set(video, fallbackTimer);

    iframe.addEventListener('load', () => {
      iframe.dataset.loaded = 'true';
    });

    iframe.addEventListener('error', () => this.#resetVideoFallback(video, 'iframe-error'), { once: true });
  }

  /** @param {HTMLElement} video */
  #markVideoPlaying(video, iframe) {
    if (this.videoFrames.get(video) !== iframe) return;
    iframe.dataset.ready = 'true';
    video.dataset.playerReady = 'true';
    this.#finishVideoLoad(video);
    video.classList.remove('is-loading', 'is-fallback');
    video.classList.add('is-playing');
    this.#pumpVideoQueue();
  }

  /**
   * @param {HTMLElement} video
   * @param {string} reason
   */
  #resetVideoFallback(video, reason) {
    const timer = this.videoLoadTimers.get(video);
    if (timer) window.clearTimeout(timer);
    this.videoLoadTimers.delete(video);

    const iframe = this.videoFrames.get(video) || video.querySelector('.sa-video__iframe');
    if (iframe) iframe.remove();
    this.videoFrames.delete(video);

    video.dataset.loaded = 'false';
    video.dataset.fallback = 'true';
    video.dataset.fallbackReason = reason;
    this.#finishVideoLoad(video);
    video.classList.remove('is-loading', 'is-playing');
    video.classList.add('is-fallback');
    this.#pumpVideoQueue();
  }

  /**
   * @param {Window | null} source
   * @returns {{ video: HTMLElement, iframe: HTMLIFrameElement } | null}
   */
  #findTrackedVideo(source) {
    if (!source) return null;
    for (const [video, iframe] of this.videoFrames.entries()) {
      if (iframe.contentWindow === source) return { video, iframe };
    }
    return null;
  }

  /** @param {HTMLElement} video */
  #finishVideoLoad(video) {
    const timer = this.videoLoadTimers.get(video);
    if (timer) window.clearTimeout(timer);
    this.videoLoadTimers.delete(video);
    if (this.videoLoading.has(video)) {
      this.videoLoading.delete(video);
      this.activeVideoLoads = Math.max(0, this.activeVideoLoads - 1);
    }
  }

  #disconnectVideoObserver() {
    if (this.videoObserver) this.videoObserver.disconnect();
    this.videoObserver = null;
    this.videoQueue.forEach((video) => {
      if (video instanceof HTMLElement) video.dataset.queued = 'false';
    });
    this.videoQueue = [];
  }

  #teardownVideos() {
    this.#disconnectVideoObserver();
    this.videoLoadTimers.forEach((timer) => window.clearTimeout(timer));
    this.videoLoadTimers.clear();
    this.videoFrames.forEach((iframe) => iframe.remove());
    this.videoFrames.clear();
    this.activeVideoLoads = 0;
  }

  /**
   * @param {ReturnType<typeof normalizeRailItem>[]} items
   * @param {boolean} [personalized]
   * @returns {string}
   */
  #renderCloneSets(items, personalized = false) {
    if (items.length < 2) return '';
    return Array.from({ length: 3 }, () => `
      <div class="sa-home-rail__set" data-sa-clone-set>
        ${this.#renderCards(items, true, personalized)}
      </div>
    `).join('');
  }

  #setupAutoScroll() {
    this.#teardownAutoScroll();
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    this.querySelectorAll('[data-sa-auto-track]').forEach((track) => {
      if (!(track instanceof HTMLElement)) return;
      this.autoScrollers.push(createAutoScroller(track));
    });
  }

  #teardownAutoScroll() {
    this.autoScrollers.forEach((destroy) => destroy());
    this.autoScrollers = [];
  }
}

function linkHomepageCatalogHeading() {
  const headings = document.querySelectorAll('.ui-test-product-list .text-block :is(h1, h2, h3, h4, h5, h6)');

  for (const heading of headings) {
    if (!(heading instanceof HTMLElement)) continue;
    if (heading.querySelector('a[href="/collections/all"]')) continue;

    const normalizedText = heading.textContent
      .replace(/\s+/g, ' ')
      .replace(/:$/, '')
      .replace(/(?:->|→)$/, '')
      .trim()
      .toUpperCase();

    if (normalizedText !== CATALOG_HEADING_TEXT) continue;

    const link = document.createElement('a');
    link.href = '/collections/all';
    link.textContent = `${CATALOG_HEADING_TEXT} →`;
    heading.textContent = '';
    heading.append(link);
  }
}

/**
 * @param {HTMLElement} track
 * @returns {() => void}
 */
function createAutoScroller(track) {
  let resumeTimer = 0;
  const strip = track.querySelector('.sa-home-rail__strip');
  const firstSet = track.querySelector('[data-sa-loop-set]');
  if (!(strip instanceof HTMLElement)) return () => {};
  const updateLoopDistance = () => {
    if (!(firstSet instanceof HTMLElement)) return;
    const gap = Number.parseFloat(window.getComputedStyle(strip).columnGap || '0') || 0;
    const loopDistance = firstSet.scrollWidth + gap;
    const durationSeconds = Math.max(
      RAIL_MARQUEE_MIN_DURATION_SECONDS,
      Math.ceil(loopDistance / RAIL_MARQUEE_TARGET_PIXELS_PER_SECOND)
    );
    strip.style.setProperty('--sa-loop-distance', `${loopDistance}px`);
    strip.style.setProperty('--sa-rail-marquee-duration', `${durationSeconds}s`);
  };
  updateLoopDistance();

  const pause = (duration = 2400) => {
    track.classList.add('is-paused');
    window.clearTimeout(resumeTimer);
    resumeTimer = window.setTimeout(() => {
      track.classList.remove('is-paused');
    }, duration);
  };
  const onInteract = () => pause(5000);
  const onResize = () => updateLoopDistance();

  track.addEventListener('pointerdown', onInteract);
  track.addEventListener('touchstart', onInteract, { passive: true });
  track.addEventListener('wheel', onInteract, { passive: true });
  track.addEventListener('focusin', onInteract);
  window.addEventListener('resize', onResize);

  return () => {
    window.clearTimeout(resumeTimer);
    track.removeEventListener('pointerdown', onInteract);
    track.removeEventListener('touchstart', onInteract);
    track.removeEventListener('wheel', onInteract);
    track.removeEventListener('focusin', onInteract);
    window.removeEventListener('resize', onResize);
  };
}

function waitForRailPageQueue() {
  return new Promise((resolve) => {
    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(() => resolve(), { timeout: 1200 });
      return;
    }
    window.setTimeout(resolve, 450);
  });
}

function roundedLocationParams(location) {
  return {
    lat: Number(Number(location.latitude).toFixed(2)),
    lng: Number(Number(location.longitude).toFixed(2)),
  };
}

/** @param {string} value */
function escapeId(value) {
  return String(value || 'rail').replace(/[^A-Za-z0-9_-]/g, '-');
}

/** @param {string} type */
function isPlayerReadyMessage(type) {
  return ['onPlayerReady', 'onStateChange', 'onCurrentTime', 'onMute', 'onVolumeChange'].includes(type);
}

function markFeaturedItem(item) {
  if (!item || typeof item !== 'object') return item;
  const business = item.business && typeof item.business === 'object' ? item.business : item;
  if (item.business) {
    return {
      ...item,
      business: {
        ...business,
        featured: true,
      },
    };
  }
  return {
    ...item,
    featured: true,
  };
}

function itemSlug(item) {
  const business = item?.business || item;
  return String(business?.slug || business?.handle || business?.shopifyMetaobjectHandle || '').trim();
}

function dedupeRailItems(items) {
  const seen = new Set();
  return items.filter((item) => {
    const slug = itemSlug(item);
    if (!slug) return true;
    if (seen.has(slug)) return false;
    seen.add(slug);
    return true;
  });
}

/** @param {string} value */
function escapeHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

if (!customElements.get('so-atlanta-homepage-rails')) {
  customElements.define('so-atlanta-homepage-rails', SoAtlantaHomepageRails);
}
